import Evaporate from 'evaporate'
import api from './server'
import { hmac, md5, sha256 } from './utils/crypto'
import errorHandler, { HttpError } from './server/error-handler'

// for details about signing requests, consult with:
// http://docs.aws.amazon.com/general/latest/gr/signature-v4-test-suite.html
// http://docs.aws.amazon.com/AmazonS3/latest/API/sig-v4-header-based-auth.html
const signRequest = (secretAccessKey: string, region: string) => {
  return async (
    _signParams,
    _signHeaders,
    stringToSign,
    signatureDateTime,
    canonicalRequest,
  ) => {
    const dateKey = hmac(
      'AWS4' + secretAccessKey,
      signatureDateTime.substr(0, 8),
      'buffer',
    )
    const dateRegionKey = hmac(dateKey, region, 'buffer')
    const dateRegionServiceKey = hmac(dateRegionKey, 's3', 'buffer')
    const signingKey = hmac(dateRegionServiceKey, 'aws4_request', 'buffer')
    const hash = sha256(canonicalRequest)
    const actualStringToSign = unescape(stringToSign).replace(
      'UNSIGNED-PAYLOAD',
      hash,
    )
    return hmac(signingKey, actualStringToSign, 'hex')
  }
}

const getFile = (
  file: File,
  name: string,
  sessionToken: string,
  progress: (p: number, stats: Record<string, any>) => any,
) => {
  const headers = {
    'x-amz-security-token': sessionToken,
    'X-Amz-User-Agent': 'aws-sdk-js/2.156.0 callback',
  }
  return {
    file,
    name,
    xAmzHeadersAtInitiate: headers,
    xAmzHeadersCommon: headers,
    progress,
  }
}

// place upload function separately so we won't include EvaporateJS and crypto
// functions to bundles that don't need them
export function uploadToS3Evaporate(
  uploadPolicy: Record<string, any>,
  file: File,
  info: Record<string, any>,
  onUploadProgress: (...args: Array<any>) => any,
  onUploadError: (...args: Array<any>) => any,
  abortSignal?: AbortSignal,
): Promise<any> {
  const {
    AccessKeyId,
    SecretAccessKey,
    SessionToken,
    Bucket,
    Key,
    RegionName,
  } = uploadPolicy
  if (abortSignal?.aborted) return Promise.reject(new Error('Upload cancelled'))
  return new Promise((resolve, reject) => {
    let evaporateInstance: any = null
    abortSignal?.addEventListener('abort', () => {
      if (evaporateInstance) evaporateInstance.cancel(`${Bucket}/${Key}`)
      reject(new Error('Upload cancelled'))
    })
    Evaporate.create({
      bucket: Bucket,
      logging: false,
      aws_key: AccessKeyId,
      computeContentMd5: true,
      cryptoMd5Method: data => md5(data),
      cryptoHexEncodedHash256: () => 'UNSIGNED-PAYLOAD',
      maxConcurrentParts: 1,
      // concurrency is handled in queue
      partSize: 10 * 1024 * 1024,
      // 10MB * 10000 parts = 100 GB max file size
      retryBackoffPower: 1.5,
      maxRetryBackoffSecs: 15,
      // don't wait too long before retrying
      progressIntervalMS: 300,
      cloudfront: true,
      awsRegion: RegionName,
      s3Acceleration: true,
      timeUrl: api.getServerTimeUrl(),
      // onlyRetryForSameFileName must be true, otherwise if user drags
      // file that was cancelled previously and but some parts left, upload
      // will be reattempted with the old key, ignoring key passed in add()
      onlyRetryForSameFileName: true,
      customAuthMethod: signRequest(SecretAccessKey, RegionName),
    })
      .then(evaporate => {
        evaporateInstance = evaporate
        return evaporate.add(
          getFile(file, Key, SessionToken, p => {
            // second param contains upload speed and some additional data
            // might be useful in future:
            // https://github.com/TTLabs/EvaporateJS/wiki/Evaporate.prototype.add()
            if (onUploadProgress) {
              onUploadProgress({ ...info, progress: p * 100 })
            }
          }),
        )
      })
      .then((awsKey: string) => resolve(awsKey))
      .catch((error: string) => {
        // we need to fire this event so other uploaded items blocked by
        // cancelled track will activate
        onUploadError(info)
        // everything fine, don't log exception
        if (error === 'User aborted the upload') return
        errorHandler(
          new HttpError(error, 400),
          'api.uploadToS3Evaporate',
          reject,
        )
      })
  })
}
